import { useState, useCallback, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { intl } from '../../../../../../../i18n'
import { useVersionReportTemplateRepository } from '../../../../../../../services/adaptors/versionReportTemplateRepositoryAdaptor'
import {
  addGlobalMessage,
  MessageLevel,
} from '../../../../../../../store/messages'
import { VersionReportTemplateEntity } from '../../../../../../../domain/entity/version-report/VersionReportTemplateEntity'

export type UseTemplateDialogProps = {
  onAfterSubmit: () => void | Promise<void>
  onClose: () => void
  original?: VersionReportTemplateEntity
}

export const useTemplateDialog = ({
  onAfterSubmit,
  onClose,
  original,
}: UseTemplateDialogProps) => {
  const [templateName, setTemplateName] = useState('')
  const [loading, setLoading] = useState(false)
  const isSubmittingRef = useRef(false)
  const dispatch = useDispatch()
  const { create, update } = useVersionReportTemplateRepository()

  useEffect(() => {
    setTemplateName(original?.name ?? '')
  }, [original])

  const handleSubmit = useCallback(async () => {
    if (isSubmittingRef.current) return
    isSubmittingRef.current = true
    try {
      setLoading(true)
      const trimmedName = templateName.trim()
      if (original) {
        await update(original.uuid, trimmedName)
        dispatch(
          addGlobalMessage({
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({ id: 'update.completed.title' }),
            text: `フォルダ「${trimmedName}」が更新されました`,
          })
        )
      } else {
        await create(trimmedName)
        dispatch(
          addGlobalMessage({
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({ id: 'registration.complete' }),
            text: `フォルダ「${trimmedName}」が作成されました`,
          })
        )
      }
      await onAfterSubmit()
      setTemplateName('')
      onClose()
    } catch (error) {
      console.error('Template save failed:', error)
      dispatch(
        addGlobalMessage({
          type: MessageLevel.ERROR,
          title: original ? 'フォルダ更新エラー' : 'フォルダ作成エラー',
          text: original
            ? 'フォルダ更新に失敗しました'
            : 'フォルダ作成に失敗しました',
        })
      )
    } finally {
      isSubmittingRef.current = false
      setLoading(false)
    }
  }, [original, templateName, create, update, dispatch, onAfterSubmit, onClose])

  const handleClose = useCallback(() => {
    if (!loading) {
      setTemplateName('')
      onClose()
    }
  }, [loading, onClose])

  return {
    templateName,
    setTemplateName,
    loading,
    handleSubmit,
    handleClose,
  }
}
